/* eslint-disable react/prop-types */
import {
  useCallback,
  useMemo,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";
import { AgGridReact } from "ag-grid-react";
import { ModuleRegistry } from "ag-grid-community";
import {
  ClientSideRowModelModule,
  ValidationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  PaginationModule,
  RowSelectionModule,
  TextEditorModule,
  SelectEditorModule,
  CustomEditorModule,
  NumberEditorModule,
  PinnedRowModule,
  CellStyleModule,
  DateEditorModule,
  RenderApiModule,
} from "ag-grid-community";
import { ColDef } from "ag-grid-community";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

// Define interfaces for props and state
interface AgGridTableProps {
  rowData?: any[] | null;
  columnDefs?: ColDef[];
  height?: string;
  enableEditing?: boolean;
  enableSelection?: boolean;
  loading?: boolean;
  onCellValueChanged?: (event: any) => void;
  pinnedBottomRowData?: any[];
  reportHeader?: any;
  leftHeaders?: string[];
  rightHeaders?: string[];
  headerData?: string[];
  footerData?: string[];
  Caption?: any;
  RptColWidthInfo?: number[];
  RptColAlignInfo?: (
    | "left"
    | "center"
    | "right"
    | "justify"
    | "fill"
    | "centerContinuous"
    | "distributed"
  )[];
  CPrintName?: string;
  CAddress1?: string;
  CAddress2?: string;
  CAddress3?: string;
  CAddress4?: string;
  onRowClick?: (rowData: any) => void;
  cursor?: string;
  domLayout?: any;
}

interface RootState {
  theme: string;
  root: any;
}

// Define HAlignType for jspdf-autotable
type HAlignType = "left" | "center" | "right";

// Register community modules
ModuleRegistry.registerModules([
  ClientSideRowModelModule,
  ValidationModule,
  TextFilterModule,
  NumberFilterModule,
  DateFilterModule,
  PaginationModule,
  RowSelectionModule,
  TextEditorModule,
  SelectEditorModule,
  CustomEditorModule,
  NumberEditorModule,
  PinnedRowModule,
  CellStyleModule,
  DateEditorModule,
  RenderApiModule,
]);

const GridTable = forwardRef(
  (
    {
      rowData = [],
      columnDefs = [],
      height = "500px",
      enableEditing = false,
      enableSelection = false,
      onCellValueChanged,
      pinnedBottomRowData = [],
      reportHeader = { RptName: "" },
      leftHeaders = [],
      rightHeaders = [],
      headerData = [],
      footerData = [],
      Caption,
      RptColWidthInfo = [],
      RptColAlignInfo = [],
      CPrintName = "",
      CAddress1 = "",
      CAddress2 = "",
      CAddress3 = "",
      CAddress4 = "",
      cursor,
      onRowClick,
      domLayout,
    }: AgGridTableProps,
    ref
  ) => {
    const gridRef = useRef<AgGridReact>(null);
    const theme = useSelector((state: RootState) => state.root.theme);
    const defaultColDef = useMemo<ColDef>(
      () => ({
        sortable: true,
        filter: true,
        resizable: true,
        editable: enableEditing,
      }),
      [enableEditing]
    );

    // Filter out Action column for exports
    // const exportColumnDefs = columnDefs.filter((col) => col.field !== "action");
    const exportColumnDefs = columnDefs.filter(
      (col) =>
        !["action", "hideShow", "view"].includes(col.field as string)
    );

    // console.log('abc', exportColumnDefs);
    const onExportExcel = useCallback(async () => {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Report");

      // Define Column Widths using RptColWidthInfo
      const CHAR_WIDTH = 10; // Pixels per character
      const MAX_WIDTH = 300; // Max pixel width
      const EXCEL_WIDTH_FACTOR = 0.15; // Conversion to Excel width units
      const columnWidths = RptColWidthInfo.map((charCount) => {
        const pixelWidth = Math.min(charCount * CHAR_WIDTH, MAX_WIDTH);
        return Math.round(pixelWidth * EXCEL_WIDTH_FACTOR);
      });

      // Fallback to dynamic width calculation if RptColWidthInfo is empty
      const headers = exportColumnDefs.map(
        (col) => col.headerName || col.field || ""
      );
      const fallbackColWidths = exportColumnDefs.map((_, index) => {
        const maxWidth = headers[index]?.length * 1.2 || 10;
        return Math.round(Math.min(Math.max(maxWidth, 10), 100));
      });

      // Set column widths
      worksheet.columns = exportColumnDefs.map((_, index) => ({
        width:
          columnWidths[index] !== undefined
            ? columnWidths[index]
            : fallbackColWidths[index],
      }));

      // Header color (Royal Blue in ARGB format)
      const headerColor = "FF4169E1";

      let currentRow = 1;

      // Add CPrintName
      if (CPrintName) {
        const row = worksheet.addRow([CPrintName]);
        row.getCell(1).style = {
          font: { bold: true, size: 16, color: { argb: "FF000000" } },
          alignment: { horizontal: "center", vertical: "middle" },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF0F0F0" },
          },
        };
        worksheet.mergeCells(
          currentRow,
          1,
          currentRow,
          exportColumnDefs.length
        );
        currentRow++;
      }

      // Add CAddress1, CAddress2 in second row, centered across all columns
      if (CAddress1 || CAddress2) {
        const addressRow1 = worksheet.addRow([
          [CAddress1, CAddress2].filter(Boolean).join(", "),
        ]);
        addressRow1.getCell(1).style = {
          font: { bold: true, size: 10, color: { argb: "FF666666" } },
          alignment: { horizontal: "center", vertical: "middle" },
        };
        worksheet.mergeCells(
          currentRow,
          1,
          currentRow,
          exportColumnDefs.length
        );
        currentRow++;
      }

      // Add CAddress3, CAddress4 in third row, centered across all columns
      if (CAddress3 || CAddress4) {
        const addressRow2 = worksheet.addRow([
          [CAddress3, CAddress4].filter(Boolean).join(", "),
        ]);
        addressRow2.getCell(1).style = {
          font: { bold: true, size: 10, color: { argb: "FF666666" } },
          alignment: { horizontal: "center", vertical: "middle" },
        };
        worksheet.mergeCells(
          currentRow,
          1,
          currentRow,
          exportColumnDefs.length
        );
        currentRow++;
      }

      // Add Report Header
      if (reportHeader?.RptName) {
        const row = worksheet.addRow([reportHeader.RptName]);
        row.getCell(1).style = {
          font: { bold: true, size: 14, color: { argb: "FF000000" } },
          alignment: { horizontal: "center", vertical: "middle" },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FFF0F0F0" },
          },
        };
        worksheet.mergeCells(
          currentRow,
          1,
          currentRow,
          exportColumnDefs.length
        );
        currentRow++;
      }

      // Add Left and Right Headers above first and last columns
      const maxHeaderLength = Math.max(leftHeaders.length, rightHeaders.length);
      if (maxHeaderLength > 0) {
        for (let i = 0; i < maxHeaderLength; i++) {
          const row = worksheet.addRow([
            ...Array(exportColumnDefs.length).fill(""),
          ]);
          if (leftHeaders[i]) {
            row.getCell(1).value = leftHeaders[i];
            row.getCell(1).style = {
              font: { bold: true, size: 10, color: { argb: "FF666666" } },
              alignment: { horizontal: "left", vertical: "middle" },
            };
          }
          if (rightHeaders[i]) {
            row.getCell(exportColumnDefs.length).value = rightHeaders[i];
            row.getCell(exportColumnDefs.length).style = {
              font: { bold: true, size: 10, color: { argb: "FF666666" } },
              alignment: { horizontal: "right", vertical: "middle" },
            };
          }
          currentRow++;
        }
        // Merge cells vertically for left and right headers if multiple headers exist
        if (leftHeaders.length > 1) {
          worksheet.mergeCells(
            currentRow - maxHeaderLength,
            1,
            currentRow - 1,
            1
          );
        }
        if (rightHeaders.length > 1) {
          worksheet.mergeCells(
            currentRow - maxHeaderLength,
            exportColumnDefs.length,
            currentRow - 1,
            exportColumnDefs.length
          );
        }
      }

      // Add Opening Balance in Last Column
      if (headerData?.length > 0) {
        headerData.forEach((header) => {
          const row = worksheet.addRow([
            ...Array(exportColumnDefs.length - 1).fill(""),
            header,
          ]);
          row.getCell(exportColumnDefs.length).style = {
            font: { bold: true, color: { argb: "FF666666" } },
            alignment: { horizontal: "right", vertical: "middle" },
          };
          currentRow++;
        });
      }

      // Add Table Headers
      const headerRow = worksheet.addRow(headers);
      headers.forEach((header, index) => {
        const cell = headerRow.getCell(index + 1);
        cell.value = header;
        cell.style = {
          font: { bold: true, color: { argb: "FFFFFFFF" } },
          fill: {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: headerColor },
          },
          alignment: {
            horizontal: "center",
            vertical: "middle",
            wrapText: true,
          },
          border: {
            top: { style: "thin", color: { argb: "FF000000" } },
            bottom: { style: "thin", color: { argb: "FF000000" } },
            left: { style: "thin", color: { argb: "FF000000" } },
            right: { style: "thin", color: { argb: "FF000000" } },
          },
        };
      });
      currentRow++;

      // Add Table Row Data
      rowData?.forEach((row) => {
        const rowData = exportColumnDefs.map(
          (col) => row[col.field || ""] || ""
        );
        const excelRow = worksheet.addRow(rowData);
        rowData.forEach((_, colIndex) => {
          excelRow.getCell(colIndex + 1).style = {
            alignment: {
              horizontal: RptColAlignInfo[colIndex] || "left",
              vertical: "middle",
              wrapText: true,
            },
          };
        });
        currentRow++;
      });

      // Add Pinned Bottom Row Data
      if (pinnedBottomRowData?.length) {
        pinnedBottomRowData.forEach((row: { [x: string]: any }) => {
          const rowData = exportColumnDefs.map(
            (col) => row[col.field || ""] || ""
          );
          const excelRow = worksheet.addRow(rowData);
          rowData.forEach((_, colIndex) => {
            excelRow.getCell(colIndex + 1).style = {
              alignment: {
                horizontal: RptColAlignInfo[colIndex] || "left",
                vertical: "middle",
                wrapText: true,
              },
            };
          });
          currentRow++;
        });
      }

      // Add Closing Balance in Last Column
      if (footerData?.length > 0) {
        footerData.forEach((footer) => {
          const row = worksheet.addRow([
            ...Array(exportColumnDefs.length - 1).fill(""),
            footer,
          ]);
          row.getCell(exportColumnDefs.length).style = {
            font: { bold: true, color: { argb: "FF666666" } },
            alignment: { horizontal: "right", vertical: "middle" },
          };
          currentRow++;
        });
      }

      // Save the workbook
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      saveAs(blob, `${Caption || reportHeader?.RptName}.xlsx`);
    }, [
      rowData,
      exportColumnDefs,
      pinnedBottomRowData,
      reportHeader,
      leftHeaders,
      rightHeaders,
      headerData,
      footerData,
      Caption,
      RptColWidthInfo,
      RptColAlignInfo,
      CPrintName,
      CAddress1,
      CAddress2,
      CAddress3,
      CAddress4,
    ]);

    const onExportPDF = useCallback(() => {
      // Calculate total table width using RptColWidthInfo
      const CHAR_WIDTH = 10;
      const MAX_WIDTH = 300;
      const MM_PER_PIXEL = 0.264583;
      const columnWidths = RptColWidthInfo.map((charCount) => {
        const pixelWidth = Math.min(charCount * CHAR_WIDTH, MAX_WIDTH);
        return pixelWidth * MM_PER_PIXEL;
      });

      // Fallback to default width if RptColWidthInfo is empty
      const totalColumns = exportColumnDefs.length;
      const defaultColumnWidth = 50;
      const finalColumnWidths = exportColumnDefs.map((_, index) =>
        columnWidths[index] !== undefined
          ? columnWidths[index]
          : defaultColumnWidth
      );

      // Calculate total table width
      const totalTableWidth = finalColumnWidths.reduce(
        (sum, width) => sum + width,
        0
      );

      // A4 page dimensions: portrait (210 mm width), landscape (297 mm width)
      const PORTRAIT_PAGE_WIDTH = 210 - 20;
      const isLandscape =
        totalTableWidth > PORTRAIT_PAGE_WIDTH || exportColumnDefs.length > 8;

      // Initialize jsPDF with dynamic orientation
      const doc = new jsPDF({
        orientation: isLandscape ? "landscape" : "portrait",
        unit: "mm",
        format: "a4",
      });

      // Calculate page width based on orientation
      const pageWidth = doc.internal.pageSize.getWidth() - 20;

      // Adjust column widths if total width exceeds page width
      const adjustedColumnWidths = finalColumnWidths.map((width) => {
        if (totalTableWidth > pageWidth) {
          return (width / totalTableWidth) * pageWidth;
        }
        return width;
      });

      // Use RptColAlignInfo for column alignments, ensuring HAlignType
      const columnAlignments = RptColAlignInfo.map((align) =>
        ["left", "center", "right"].includes(align)
          ? (align as HAlignType)
          : "left"
      );

      // Font size adjustment based on column count
      const fontSize = totalColumns > 10 ? 8 : 10;

      // Headers and Data
      const headers = [
        exportColumnDefs.map((col) => col.headerName || col.field || ""),
      ];
      const body =
        rowData?.map((row) =>
          exportColumnDefs.map((col) => row[col.field || ""] || "")
        ) || [];

      // Pinned Bottom
      if (pinnedBottomRowData?.length) {
        pinnedBottomRowData.forEach((row: { [x: string]: any }) => {
          const rowData = exportColumnDefs.map(
            (col) => row[col.field || ""] || ""
          );
          body.push(rowData);
        });
      }

      // Track if headers have been drawn
      let isFirstPage = true;

      // Calculate initial Y position for headers on first page
      let currentY = 10;

      // Add headers only on the first page
      if (isFirstPage) {
        // Add CPrintName
        if (CPrintName) {
          doc.setFontSize(16);
          doc.setFont("helvetica", "bold");
          doc.text(CPrintName, pageWidth / 2 + 10, currentY, {
            align: "center",
          });
          currentY += 8;
        }

        // Add CAddress1, CAddress2 centered
        if (CAddress1 || CAddress2) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100, 100, 100);
          const address1 = [CAddress1, CAddress2].filter(Boolean).join(", ");
          if (address1) {
            doc.text(address1, pageWidth / 2 + 10, currentY, {
              align: "center",
            });
          }
          currentY += 6;
        }

        // Add CAddress3, CAddress4 centered
        if (CAddress3 || CAddress4) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100, 100, 100);
          const address2 = [CAddress3, CAddress4].filter(Boolean).join(", ");
          if (address2) {
            doc.text(address2, pageWidth / 2 + 10, currentY, {
              align: "center",
            });
          }
          currentY += 6;
        }

        // Report Header
        if (reportHeader?.RptName) {
          doc.setFontSize(14);
          doc.setFont("helvetica", "bold");
          doc.text(reportHeader.RptName, pageWidth / 2 + 10, currentY, {
            align: "center",
          });
          currentY += 8;
          doc.setFontSize(12);
        }

        // Left and Right Headers
        if (leftHeaders.length > 0 || rightHeaders.length > 0) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "normal");
          doc.setTextColor(100, 100, 100);
          const maxHeaderLength = Math.max(
            leftHeaders.length,
            rightHeaders.length
          );
          for (let i = 0; i < maxHeaderLength; i++) {
            if (leftHeaders[i]) {
              doc.text(leftHeaders[i], 10, currentY);
            }
            if (rightHeaders[i]) {
              doc.text(rightHeaders[i], pageWidth + 10, currentY, {
                align: "right",
              });
            }
            currentY += 6;
          }
        }

        // Opening Balance
        if (headerData?.length > 0) {
          doc.setFontSize(10);
          doc.setFont("helvetica", "bold");
          headerData.forEach((header) => {
            doc.text(header, pageWidth + 10, currentY, { align: "right" });
            currentY += 6;
          });
        }
      }

      // Table
      autoTable(doc, {
        head: headers,
        body: body,
        theme: "striped",
        styles: {
          fontSize: fontSize,
          cellPadding: 2,
          overflow: "linebreak",
        },
        headStyles: {
          fillColor: [100, 100, 100],
          textColor: [255, 255, 255],
          fontStyle: "bold",
        },
        columnStyles: exportColumnDefs.reduce((acc, _, index) => {
          acc[index.toString()] = {
            cellWidth: adjustedColumnWidths[index],
            halign: columnAlignments[index],
          };
          return acc;
        }, {} as { [key: string]: { cellWidth: number; halign: HAlignType } }),
        margin: { top: isFirstPage ? currentY : 10, left: 10, right: 10 },
        pageBreak: "auto",
        rowPageBreak: "avoid",
        tableWidth: pageWidth,
        didDrawPage: (data) => {
          // Set flag to false after first page
          isFirstPage = false;

          // Add page number
          doc.setFontSize(8);
          doc.text(
            `Page ${data.pageNumber}`,
            doc.internal.pageSize.getWidth() - 20,
            doc.internal.pageSize.getHeight() - 10
          );

          // Reset margin for subsequent pages
          data.settings.margin.top = 10;
        },
      });

      // Closing Balance (after table)
      if (footerData?.length > 0) {
        const tableHeight = doc.lastAutoTable.finalY || currentY;
        doc.setFontSize(10);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(100, 100, 100);
        footerData.forEach((footer, index) => {
          doc.text(footer, pageWidth + 10, tableHeight + 6 + index * 6, {
            align: "right",
          });
        });
      }

      // Save PDF
      doc.save(`${Caption}.pdf`);
    }, [
      rowData,
      exportColumnDefs,
      pinnedBottomRowData,
      reportHeader,
      leftHeaders,
      rightHeaders,
      headerData,
      footerData,
      Caption,
      RptColWidthInfo,
      RptColAlignInfo,
      CPrintName,
      CAddress1,
      CAddress2,
      CAddress3,
      CAddress4,
    ]);

    const onSelectionChanged = useCallback(() => {
      if (enableSelection) {
        const selectedRows = gridRef.current?.api.getSelectedRows();
        console.log("Selected Rows:", selectedRows);
      }
    }, [enableSelection]);

    useImperativeHandle(ref, () => ({
      exportToExcel: onExportExcel,
      exportToPDF: onExportPDF,
    }));

    const themeClass =
      theme === "dark_mode" ? "ag-theme-quartz-dark" : "ag-theme-quartz";

    return (
      <>
        <style>
          {`
            .ag-theme-quartz-dark {
              --ag-background-color: #1d1d42 !important;
              --ag-header-background-color: #1d1d42 !important;
              --ag-row-hover-color: rgba(255, 255, 255, 0.1) !important;
              --ag-column-hover-color: rgba(255, 255, 255, 0.1) !important;
              --ag-row-background-color: #2a2a4a !important;
              --ag-odd-row-background-color: #3a3a5a !important;
                 --ag-row-height: calc(var(--ag-font-size) + var(--ag-grid-size) * 2.0) !important;
                   --ag-header-height: calc(var(--ag-font-size) + var(--ag-grid-size) * 2.25);
            }
            .ag-theme-quartz {
              --ag-header-background-color: #004588 !important;
              --ag-header-foreground-color: #ffffff !important;
              --ag-row-background-color: #ffffff !important;
              --ag-odd-row-background-color: #f0f4f8 !important;
               --ag-row-height: calc(var(--ag-font-size) + var(--ag-grid-size) * 2.0) !important;
                   --ag-header-height: calc(var(--ag-font-size) + var(--ag-grid-size) * 2.25);
                   --ag-header-column-resize-handle-color: #ffffff !important;
            }
            .ag-theme-quartz .ag-icon-filter {
              color: #ffffff !important;
            }
            .ag-theme-quartz .ag-header-cell .ag-filter-icon .ag-icon {
              fill: #ffffff !important;
            }
          
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
           .${themeClass} .ag-row, .${themeClass} .ag-row-even, .${themeClass} .ag-row-odd {
        
            cursor: ${cursor !== undefined ? "pointer" : "default"} !important;
            }
            .${themeClass} .ag-row:hover {
              background-color: ${theme === "dark_mode"
              ? "rgba(255, 255, 255, 0.2)"
              : "rgba(0, 0, 0, 0.05)"
            } !important;
            }
          `}
        </style>
        <div className={`table-responsive ${themeClass}`} style={{ height }}>
          <AgGridReact
            suppressDragLeaveHidesColumns={true}
            ref={gridRef}
            rowData={rowData}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            pagination={false}
            animateRows={true}
            rowModelType="clientSide"
            theme="legacy"
            rowSelection={enableSelection ? "multiple" : undefined}
            onSelectionChanged={
              enableSelection ? onSelectionChanged : undefined
            }
            onCellValueChanged={onCellValueChanged}
            pinnedBottomRowData={pinnedBottomRowData}
            onCellClicked={
              onRowClick
                ? (event) => {
                  if (event) {
                    onRowClick(event);
                  }
                }
                : undefined
            }
            domLayout={domLayout}
          />
        </div>
      </>
    );
  }
);

export default GridTable;
